import { useState } from "react";
import {
  Check,
  X,
  ArrowRight,
  CheckCircle2,
  Trash2,
  Send,
  UserCheck,
  AlertCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useHandshake,
  useAcknowledgeHandshake,
  useConfirmHandshake,
  useCloseHandshake,
  useRejectHandshake,
  useCancelHandshake,
  usePostMessage,
} from "@/hooks/useHandshakes";
import { fmtMoney, statusBadgeVariant, handshakeStatusIcon } from "./helpers";



// ─── Handshake drawer ─────────────────────────────────────────────

export function HandshakeDrawer({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { data: handshake, isLoading } = useHandshake(id);
  const [messageText, setMessageText] = useState("");

  const acknowledge = useAcknowledgeHandshake();
  const confirmHs = useConfirmHandshake();
  const closeHs = useCloseHandshake();
  const reject = useRejectHandshake();
  const cancel = useCancelHandshake();
  const postMsg = usePostMessage();

  if (!handshake && !isLoading) return null;

  return (
    <Sheet open={!!id} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        {isLoading || !handshake ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-base">
                  {handshake.handshakeId}
                </span>
                <Badge
                  variant={statusBadgeVariant(handshake.status)}
                  className="gap-1"
                >
                  {handshakeStatusIcon(handshake.status)}
                  {handshake.status}
                </Badge>
              </SheetTitle>
              <SheetDescription>
                {handshake.myRole === "distributor" ? "Outgoing" : "Incoming"} ·{" "}
                {fmtMoney(handshake.grandTotalMinor, handshake.currency)}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Lines */}
              <section>
                <h4 className="text-sm font-medium mb-3">Line items</h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2 font-medium">Product</th>
                        <th className="text-right p-2 font-medium">Qty</th>
                        <th className="text-right p-2 font-medium">Unit</th>
                        <th className="text-right p-2 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {handshake.lines.map((line, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{line.productName}</td>
                          <td className="p-2 text-right">{line.quantity}</td>
                          <td className="p-2 text-right">
                            {fmtMoney(line.unitPriceMinor, handshake.currency)}
                          </td>
                          <td className="p-2 text-right font-medium">
                            {fmtMoney(line.lineTotalMinor, handshake.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/30">
                        <td colSpan={3} className="p-2 text-right font-medium">
                          Grand total
                        </td>
                        <td className="p-2 text-right font-semibold">
                          {fmtMoney(
                            handshake.grandTotalMinor,
                            handshake.currency,
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>

              <Separator />

              {/* Actions */}
              <section>
                <h4 className="text-sm font-medium mb-3">Actions</h4>
                <div className="grid grid-cols-2 gap-2">
                  {/* Vendor: acknowledge or reject */}
                  {handshake.status === "requested" &&
                    handshake.myRole === "vendor" && (
                      <>
                        <Button
                          onClick={() =>
                            acknowledge.mutate(
                              { id: handshake._id, input: {} },
                              {
                                onSuccess: () =>
                                  toast.success("Handshake acknowledged"),
                                onError: (e: any) =>
                                  toast.error(
                                    e?.response?.data?.message || "Failed",
                                  ),
                              },
                            )
                          }
                        >
                          <Check className="h-4 w-4 mr-2" /> Acknowledge
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => {
                            const reason = prompt("Reason for rejection:");
                            if (reason === null) return;
                            reject.mutate(
                              { id: handshake._id, reason },
                              {
                                onSuccess: () => toast.success("Rejected"),
                                onError: (e: any) =>
                                  toast.error(
                                    e?.response?.data?.message || "Failed",
                                  ),
                              },
                            );
                          }}
                        >
                          <X className="h-4 w-4 mr-2" /> Reject
                        </Button>
                      </>
                    )}

                  {/* Distributor: confirm or cancel */}
                  {handshake.status === "acknowledged" &&
                    handshake.myRole === "distributor" && (
                      <Button
                        className="col-span-2"
                        onClick={() =>
                          confirmHs.mutate(
                            { id: handshake._id },
                            {
                              onSuccess: () => toast.success("Confirmed"),
                              onError: (e: any) =>
                                toast.error(
                                  e?.response?.data?.message || "Failed",
                                ),
                            },
                          )
                        }
                      >
                        <ArrowRight className="h-4 w-4 mr-2" /> Confirm — goods
                        can dispatch
                      </Button>
                    )}

                  {/* Distributor: close (received) */}
                  {handshake.status === "confirmed" &&
                    handshake.myRole === "distributor" && (
                      <Button
                        className="col-span-2"
                        onClick={() => {
                          if (
                            !confirm(
                              "Mark goods as received? This will increase your stock atomically.",
                            )
                          )
                            return;
                          closeHs.mutate(
                            { id: handshake._id },
                            {
                              onSuccess: () =>
                                toast.success("Closed — stock updated"),
                              onError: (e: any) =>
                                toast.error(
                                  e?.response?.data?.message || "Failed",
                                ),
                            },
                          );
                        }}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" /> Close — goods
                        received
                      </Button>
                    )}

                  {/* Distributor: cancel (non-terminal states) */}
                  {!["closed", "rejected", "cancelled"].includes(
                    handshake.status,
                  ) &&
                    handshake.myRole === "distributor" && (
                      <Button
                        variant="outline"
                        className="col-span-2"
                        onClick={() => {
                          const reason = prompt("Cancel reason (optional):");
                          if (reason === null) return;
                          cancel.mutate(
                            { id: handshake._id, reason },
                            {
                              onSuccess: () => toast.success("Cancelled"),
                              onError: (e: any) =>
                                toast.error(
                                  e?.response?.data?.message || "Failed",
                                ),
                            },
                          );
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Cancel handshake
                      </Button>
                    )}

                  {/* Terminal: nothing to do */}
                  {["closed", "rejected", "cancelled"].includes(
                    handshake.status,
                  ) && (
                    <p className="col-span-2 text-sm text-muted-foreground text-center py-2">
                      No further actions available for{" "}
                      <span className="font-medium">{handshake.status}</span>{" "}
                      handshakes.
                    </p>
                  )}
                </div>
              </section>

              <Separator />

              {/* Conversation */}
              <section>
                <h4 className="text-sm font-medium mb-3">Conversation</h4>
                <ul className="space-y-3 text-sm mb-4">
                  {handshake.conversation.map((entry, idx) => (
                    <li key={entry._id || idx} className="flex gap-3">
                      <div className="shrink-0 mt-0.5">
                        {entry.kind === "state_transition" && (
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        {entry.kind === "message" && (
                          <Send className="h-4 w-4 text-muted-foreground" />
                        )}
                        {entry.kind === "delegation" && (
                          <UserCheck className="h-4 w-4 text-muted-foreground" />
                        )}
                        {entry.kind === "receipt_generated" && (
                          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                        )}
                        {entry.kind === "system" && (
                          <AlertCircle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium">{entry.title}</p>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {formatDistanceToNow(new Date(entry.at), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                        {entry.body && (
                          <p className="text-muted-foreground text-sm mt-0.5">
                            {entry.body}
                          </p>
                        )}
                        {entry.actorName && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            by {entry.actorName}
                            {entry.actorSide && (
                              <span className="capitalize">
                                {" "}
                                ({entry.actorSide})
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Post message */}
                {!["rejected", "cancelled"].includes(handshake.status) && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          !e.shiftKey &&
                          messageText.trim()
                        ) {
                          e.preventDefault();
                          postMsg.mutate(
                            { id: handshake._id, body: messageText.trim() },
                            {
                              onSuccess: () => {
                                setMessageText("");
                                toast.success("Message sent");
                              },
                              onError: (e: any) =>
                                toast.error(
                                  e?.response?.data?.message || "Failed",
                                ),
                            },
                          );
                        }
                      }}
                    />
                    <Button
                      disabled={!messageText.trim()}
                      onClick={() => {
                        postMsg.mutate(
                          { id: handshake._id, body: messageText.trim() },
                          {
                            onSuccess: () => {
                              setMessageText("");
                              toast.success("Message sent");
                            },
                            onError: (e: any) =>
                              toast.error(
                                e?.response?.data?.message || "Failed",
                              ),
                          },
                        );
                      }}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

